import React from 'react';
import { ExclamationCircleIcon } from '@heroicons/react/24/solid';
import { XCircleIcon } from '@heroicons/react/24/outline';

/**
 * FormError Component
 * Displays field-level validation errors with consistent styling and accessibility
 *
 * @param {Object} props
 * @param {string} props.error - Error message to display
 * @param {boolean} props.visible - Whether to show the error (default: true if error exists)
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.id - ID for ARIA association
 * @param {string} props.variant - Error variant: 'inline' | 'block' (default: 'inline')
 * @param {boolean} props.icon - Whether to show error icon (default: true)
 */
const FormError = ({
  error,
  visible,
  className = '',
  id,
  variant = 'inline',
  icon = true,
}) => {
  // Don't render if no error or not visible
  const shouldShow = visible !== undefined ? visible : !!error;
  if (!shouldShow || !error) {
    return null;
  }

  const baseClasses = 'text-red-600 text-sm flex items-start gap-1.5';
  const variantClasses = {
    inline: 'mt-1',
    block: 'mt-2 p-3 bg-red-50 border border-red-200 rounded-lg',
  };

  return (
    <div
      className={`${baseClasses} ${variantClasses[variant]} ${className}`}
      role="alert"
      aria-live="polite"
      id={id}
    >
      {icon && (
        <ExclamationCircleIcon
          className="h-5 w-5 flex-shrink-0"
          aria-hidden="true"
        />
      )}
      <span className="flex-1">{error}</span>
    </div>
  );
};

/**
 * FormErrorList Component
 * Displays multiple validation errors in a list format
 *
 * @param {Object} props
 * @param {Array<string>} props.errors - Array of error messages
 * @param {string} props.title - Title for the error list
 * @param {string} props.className - Additional CSS classes
 */
export const FormErrorList = ({
  errors = [],
  title = 'Please fix the following errors:',
  className = '',
}) => {
  if (!errors || errors.length === 0) {
    return null;
  }

  return (
    <div
      className={`bg-red-50 border border-red-200 rounded-lg p-4 ${className}`}
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <XCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
        </div>
        <div className="ml-3">
          <h3 className="text-sm font-medium text-red-800">{title}</h3>
          <div className="mt-2 text-sm text-red-700">
            <ul className="list-disc pl-5 space-y-1">
              {errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * FormErrorBanner Component
 * Displays a prominent error banner at the top of forms
 *
 * @param {Object} props
 * @param {string} props.message - Error message
 * @param {Function} props.onDismiss - Callback when user dismisses the banner
 * @param {string} props.className - Additional CSS classes
 */
export const FormErrorBanner = ({
  message,
  onDismiss,
  className = '',
}) => {
  if (!message) {
    return null;
  }

  return (
    <div
      className={`bg-red-50 border-l-4 border-red-400 p-4 rounded ${className}`}
      role="alert"
      aria-live="assertive"
    >
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <XCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
        </div>
        <div className="ml-3 flex-1">
          <p className="text-sm text-red-700">{message}</p>
        </div>
        {onDismiss && (
          <div className="ml-auto pl-3">
            <button
              type="button"
              onClick={onDismiss}
              className="inline-flex rounded-md bg-red-50 p-1.5 text-red-500 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2 focus:ring-offset-red-50"
              aria-label="Dismiss error"
            >
              <span className="sr-only">Dismiss</span>
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * ValidationSummary Component
 * Displays a summary of validation errors with field names
 *
 * @param {Object} props
 * @param {Object} props.errors - Object with field names as keys and error messages as values
 * @param {Function} props.onFieldClick - Callback when user clicks on a field error
 * @param {string} props.className - Additional CSS classes
 */
export const ValidationSummary = ({
  errors = {},
  onFieldClick,
  className = '',
}) => {
  const errorEntries = Object.entries(errors).filter(([_, error]) => error);

  if (errorEntries.length === 0) {
    return null;
  }

  return (
    <div
      className={`bg-red-50 border border-red-200 rounded-lg p-4 ${className}`}
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <XCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
        </div>
        <div className="ml-3 flex-1">
          <h3 className="text-sm font-medium text-red-800">
            {errorEntries.length} {errorEntries.length === 1 ? 'field' : 'fields'} need{errorEntries.length === 1 ? 's' : ''} attention
          </h3>
          <div className="mt-2 text-sm text-red-700">
            <ul className="space-y-1">
              {errorEntries.map(([field, error]) => (
                <li key={field}>
                  {onFieldClick ? (
                    <button
                      type="button"
                      onClick={() => onFieldClick(field)}
                      className="text-red-700 hover:text-red-800 underline text-left"
                    >
                      {error}
                    </button>
                  ) : (
                    <span>{error}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * InlineFieldError Component
 * Compact inline error display for use in tight spaces
 *
 * @param {Object} props
 * @param {string} props.error - Error message
 * @param {string} props.className - Additional CSS classes
 */
export const InlineFieldError = ({ error, className = '' }) => {
  if (!error) return null;

  return (
    <span className={`text-xs text-red-600 ${className}`} role="alert">
      {error}
    </span>
  );
};

/**
 * FieldErrorTooltip Component
 * Shows error in a tooltip-style popover
 *
 * @param {Object} props
 * @param {string} props.error - Error message
 * @param {boolean} props.show - Whether to show the tooltip
 * @param {string} props.className - Additional CSS classes
 */
export const FieldErrorTooltip = ({ error, show, className = '' }) => {
  if (!error || !show) return null;

  return (
    <div
      className={`absolute z-10 mt-1 px-3 py-2 text-xs text-white bg-red-600 rounded-lg shadow-lg ${className}`}
      role="tooltip"
      aria-live="polite"
    >
      <div className="relative">
        {error}
        <div className="absolute -top-1 left-4 transform -translate-x-1/2 -translate-y-full">
          <div className="border-4 border-transparent border-b-red-600"></div>
        </div>
      </div>
    </div>
  );
};

export default FormError;